import { z } from 'zod';

// Plan Family - broad categories for builder tracking and product filtering
export const PLAN_FAMILY = {
  MEDICARE: 'medicare',
  MEDICAID: 'medicaid',
} as const;

export const PlanFamilySchema = z.enum([PLAN_FAMILY.MEDICARE, PLAN_FAMILY.MEDICAID]);

export type PlanFamily = z.infer<typeof PlanFamilySchema>;

export const PLAN_FAMILY_ORDER: readonly PlanFamily[] = [
  PLAN_FAMILY.MEDICARE,
  PLAN_FAMILY.MEDICAID,
];

// Carrier Type - specific plan types for insurance records and discovery
export const CARRIER_TYPE = {
  MEDICARE: 'medicare',
  MEDICARE_ADVANTAGE: 'medicare_advantage',
  MEDICAID: 'medicaid',
  MEDICAID_MANAGED_CARE: 'medicaid_managed_care',
} as const;

export const CarrierTypeSchema = z.enum([
  CARRIER_TYPE.MEDICARE,
  CARRIER_TYPE.MEDICARE_ADVANTAGE,
  CARRIER_TYPE.MEDICAID,
  CARRIER_TYPE.MEDICAID_MANAGED_CARE,
]);

export type CarrierType = z.infer<typeof CarrierTypeSchema>;

export const SERVICED_CARRIER_TYPES: ReadonlySet<CarrierType> = new Set([
  CARRIER_TYPE.MEDICARE,
  CARRIER_TYPE.MEDICARE_ADVANTAGE,
  CARRIER_TYPE.MEDICAID,
  CARRIER_TYPE.MEDICAID_MANAGED_CARE,
]);

export const CARRIER_TYPE_ORDER: readonly CarrierType[] = [
  CARRIER_TYPE.MEDICAID,
  CARRIER_TYPE.MEDICAID_MANAGED_CARE,
  CARRIER_TYPE.MEDICARE,
  CARRIER_TYPE.MEDICARE_ADVANTAGE,
];

// Plan family mapping - maps specific carrier types to their plan family
export function carrierTypeToFamily(type: CarrierType): PlanFamily {
  switch (type) {
    case CARRIER_TYPE.MEDICARE:
    case CARRIER_TYPE.MEDICARE_ADVANTAGE:
      return PLAN_FAMILY.MEDICARE;
    case CARRIER_TYPE.MEDICAID:
    case CARRIER_TYPE.MEDICAID_MANAGED_CARE:
      return PLAN_FAMILY.MEDICAID;
  }
}

export function carrierTypesShareFamily(first: CarrierType, second: CarrierType): boolean {
  return carrierTypeToFamily(first) === carrierTypeToFamily(second);
}

// Returns the base and managed carrier types for a plan family
export function planFamilyToCarrierTypes(family: PlanFamily): {
  base: CarrierType;
  managed: CarrierType;
} {
  return family === PLAN_FAMILY.MEDICARE
    ? { base: CARRIER_TYPE.MEDICARE, managed: CARRIER_TYPE.MEDICARE_ADVANTAGE }
    : { base: CARRIER_TYPE.MEDICAID, managed: CARRIER_TYPE.MEDICAID_MANAGED_CARE };
}

export function expandCarrierTypesToPlanFamilies(carrierTypes: Set<CarrierType>): Set<CarrierType> {
  const expandedTypes = new Set<CarrierType>();

  for (const carrierType of carrierTypes) {
    const { base, managed } = planFamilyToCarrierTypes(carrierTypeToFamily(carrierType));
    expandedTypes.add(base);
    expandedTypes.add(managed);
  }

  return expandedTypes;
}

export function parseCarrierType(str: string): CarrierType | undefined {
  return Object.values(CARRIER_TYPE).find((type) => type.toLowerCase() === str.toLowerCase());
}

export function prettyTypeName(type: CarrierType): string {
  switch (type) {
    case CARRIER_TYPE.MEDICARE:
      return 'Medicare';
    case CARRIER_TYPE.MEDICARE_ADVANTAGE:
      return 'Medicare Advantage';
    case CARRIER_TYPE.MEDICAID:
      return 'Medicaid';
    case CARRIER_TYPE.MEDICAID_MANAGED_CARE:
      return 'Medicaid Managed Care';
  }
}

export function prettyFamilyName(family: PlanFamily): string {
  switch (family) {
    case PLAN_FAMILY.MEDICARE:
      return 'Medicare';
    case PLAN_FAMILY.MEDICAID:
      return 'Medicaid';
  }
}

// Insurance Carrier schema for validation
const InsuranceCarrierSchema = z.object({
  internalId: z.string(),
  uuid: z.string(),
  name: z.string(),
  state: z.string(),
  carrierType: CarrierTypeSchema,
  displayName: z.string(),
  templateUuid: z.string(),
  isOther: z.boolean().default(false),
});

export type InsuranceCarrier = z.infer<typeof InsuranceCarrierSchema>;

export function groupByType(carriers: InsuranceCarrier[]): Map<CarrierType, InsuranceCarrier[]> {
  return carriers.reduce((acc, carrier) => {
    const type = carrier.carrierType;
    const existing = acc.get(type) ?? [];
    acc.set(type, [...existing, carrier]);
    return acc;
  }, new Map<CarrierType, InsuranceCarrier[]>());
}

// TODO Verified and unverified insurance should be the same shapes with a `verified` flag.
//  But we never get uuids back for verified insurance (a flaw in the backend design),
//  so we have to have distinct shapes for each. fml

const VerifiedInsuranceResolveSourceSchema = z.enum(['discovery', 'manual']);

// Verified Insurance - a patient's insurance policy, the insurance card
export const VerifiedInsuranceSchema = z.object({
  carrier: z.object({
    name: z.string(),
    state: z.string(),
    carrierType: CarrierTypeSchema,
  }),
  memberId: z.string(),
  verificationKey: z.string(),
  resolveSource: VerifiedInsuranceResolveSourceSchema.optional(),
});
export type VerifiedInsurance = z.infer<typeof VerifiedInsuranceSchema>;

// Queued Insurance - an insurance a patient has input but we haven't tried to verify
export const QueuedInsuranceSchema = z.object({
  carrier: InsuranceCarrierSchema,
  memberId: z.string(),
});
export type QueuedInsurance = z.infer<typeof QueuedInsuranceSchema>;

// Unverified Insurance - a patient's insurance's input we tried to verify but it failed verification
export const UnverifiedInsuranceSchema = z.object({
  carrier: InsuranceCarrierSchema,
  memberId: z.string(),
  verificationKey: z.string(),
  verifiable: z.boolean().default(true),
});
export type UnverifiedInsurance = z.infer<typeof UnverifiedInsuranceSchema>;

// Unprovided Insurance - an insurance a patient couldn't provide. This is a terminal/send-link
// state and does not count as coverage for product selection or scheduling.
export const UnprovidedInsuranceSchema = z.object({
  carrierType: CarrierTypeSchema,
  carrier: InsuranceCarrierSchema.optional(),
  memberId: z.string().optional(),
});
export type UnprovidedInsurance = z.infer<typeof UnprovidedInsuranceSchema>;

//
// Helpers for all types
//
export function hasCarrierType(
  insurance: { carrier: { carrierType: CarrierType } },
  type: CarrierType
): boolean {
  return insurance.carrier.carrierType === type;
}
